import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Save } from 'lucide-react';
import { Category, NewCategory } from './types';

interface CategoryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: NewCategory) => void;
  onUpdate: (data: Partial<Category>) => void;
  category: Category | null;
}

export const CategoryDialog = ({
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  category,
}: CategoryDialogProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const isEditMode = !!category;

  useEffect(() => {
    if (isOpen && category) {
      setName(category.name);
      setDescription(category.description || '');
    } else if (isOpen) {
      setName('');
      setDescription('');
    }
  }, [isOpen, category]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (isEditMode) {
      onUpdate({ id: category.id, name, description });
    } else {
      onCreate({ name, description });
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Category' : 'Create Category'}</DialogTitle>
          <DialogDescription>
            {isEditMode ? 'Update the details for this category.' : 'Add a new category to organize your questions.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Category Name</Label>
            <Input
              placeholder="Enter category name..."
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Description (Optional)</Label>
            <Textarea
              placeholder="Describe this category..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            disabled={!name.trim()}
            onClick={handleSubmit}
          >
            <Save className="h-4 w-4 mr-2" />
            {isEditMode ? 'Save Changes' : 'Create Category'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};